import { useEffect, useMemo, useState } from 'react';
import { useRoute, useLocation } from 'wouter';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Chip,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Input,
  RadioGroup,
  Radio,
} from '@nextui-org/react';
import {
  getClaimById,
  distributeClaimStatus,
  undistributeClaimStatus,
} from 'common/src/api/claims';
import { supabase } from 'common/src/supabase';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import { Icon } from '@iconify/react';
import { formatMoneyUSD } from '../../../utils/functions';
import { toast } from 'react-toastify';
import { getWeekKey } from 'common/src/utils/functions';
import * as XLSX from 'xlsx';
import Header from '../../../components/layouts/Header';
import {
  BackButton,
  EmptyState,
  LoadingState,
  PageLayout,
  useConfirm,
} from '../../../components/ui';

interface Claim {
  id: number;
  exporter_id: number;
  receiver: string;
  season: string;
  status: string;
  notes?: string;
  description?: string;
  created_at: string;
  submitted_at?: string;
  distributed_at?: string;
  claims_details?: ClaimDetail[];
  sinister_filter?: string;
  shipment_type?: string;
  incoterm?: string;
  comparison_type?: string;
  comparison_clients?: string[];
  product_definition?: string;
  total_claim_usd?: number;
  total_accepted_usd?: number;
  currency?: string;
  comparison_target?: string;
  filtered_clients?: string[];
  filtered_destination_countries?: string[];
  filtered_varieties?: string[];
  filtered_calibers?: string[];
  filtered_ship_trips?: string[];
  market_filter?: string[];
  include_revenues_main?: boolean;
  include_revenues_comparison?: boolean;
  include_market_in_air?: boolean;
  // Sinister-specific (only set when comparison_type starts with 'sinister_')
  pool_methodology?: string | null;
  market_reference_date?: string | null;
  exchange_rate_used?: number | null;
  date_grouping_type?: string;
}

interface ManualRevenue {
  id: number;
  settlement_detail_id: number;
  revenue_type: string;
  description?: string;
  amount_local: number;
  amount_usd?: number;
  group_id?: string;
  distribution?: string;
  settlements_details?: {
    id?: number;
    variety?: string;
    caliber?: string;
    color?: string;
    category?: string;
    format?: string;
    label?: string;
    pallet?: string;
    incoterm?: string;
    kilos?: number;
    local_total_price?: number;
    settlements?: {
      id?: number;
      reference_number?: string;
      container_number?: string;
      receiver?: string;
      exchange_rate?: number;
    };
  };
}

interface ClaimDetail {
  id: number;
  claim_id: number;
  settlement_detail_id: number;
  comparable_fob_per_kg: number;
  comparable_kilos: number;
  own_value_per_kg?: number;
  settlements_details?: {
    id?: number;
    variety?: string;
    caliber?: string;
    format?: string;
    color?: string;
    label?: string;
    kilos?: number;
    usd_total_fob?: number;
    settlement_id?: number;
    settlements?: {
      id?: number;
      exchange_rate?: number;
      reference_number?: string;
      ship_trips?: {
        eta?: string;
        etd?: string;
        ships?: {
          name?: string;
          type?: string;
        };
      };
      date_sold?: string;
    };
  };
}

const ClaimDetailPage = () => {
  const [, params] = useRoute('/settlements/claims/:id');
  const [claim, setClaim] = useState<Claim | null>(null);
  const confirm = useConfirm();
  const [isLoading, setIsLoading] = useState(true);
  const { userProfile } = useUserStore();
  const [, setLocation] = useLocation();
  const {
    isOpen: isDistributeOpen,
    onOpen: onDistributeOpen,
    onClose: onDistributeClose,
  } = useDisclosure();
  const [isDistributing, setIsDistributing] = useState(false);
  const [isUndistributing, setIsUndistributing] = useState(false);
  const [exchangeRateMode, setExchangeRateMode] = useState<
    'fixed' | 'per_settlement'
  >('per_settlement');
  const [fixedExchangeRate, setFixedExchangeRate] = useState<string>('');
  const [shipTripLabels, setShipTripLabels] = useState<Record<string, string>>(
    {}
  );
  const [manualRevenues, setManualRevenues] = useState<ManualRevenue[]>([]);

  useEffect(() => {
    const fetchClaim = async () => {
      if (!params?.id) return;

      setIsLoading(true);
      try {
        const claimData = await getClaimById(parseInt(params.id));
        setClaim(claimData);

        if (claimData?.comparison_type === 'manual_injection') {
          const { data: revenueRows, error: revenueError } = await supabase
            .from('settlement_detail_revenues')
            .select(
              `
              *,
              settlements_details(
                id,
                variety,
                caliber,
                color,
                category,
                format,
                label,
                pallet,
                incoterm,
                kilos,
                local_total_price,
                settlements(
                  id,
                  reference_number,
                  container_number,
                  receiver,
                  exchange_rate
                )
              )
            `
            )
            .eq('claim_id', claimData.id)
            .order('id', { ascending: true });

          if (revenueError) throw revenueError;
          setManualRevenues((revenueRows || []) as ManualRevenue[]);
        } else {
          setManualRevenues([]);
        }

        // Fetch ship trip labels if filtered_ship_trips exist
        if (
          claimData.filtered_ship_trips &&
          claimData.filtered_ship_trips.length > 0
        ) {
          const { data: trips } = await supabase
            .from('ship_trips')
            .select('id, eta, ships(name)')
            .in('id', claimData.filtered_ship_trips);

          if (trips) {
            const labels: Record<string, string> = {};
            trips.forEach(
              (trip: { id: number | string; eta?: string; ships?: { name?: string } }) => {
              const shipName = trip.ships?.name || 'Sin nave';
              const eta = trip.eta
                ? new Date(trip.eta).toLocaleDateString('es-CL', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })
                : '';
              labels[String(trip.id)] = [shipName, eta]
                .filter(Boolean)
                .join(' · ');
              }
            );
            setShipTripLabels(labels);
          }
        }
      } catch (error) {
        console.error('Error fetching claim:', error);
        toast.error('Error al cargar la compensación');
      } finally {
        setIsLoading(false);
      }
    };

    fetchClaim();
  }, [params?.id]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'warning';
      case 'submitted':
        return 'primary';
      case 'accepted':
        return 'success';
      case 'denied':
        return 'danger';
      case 'distributed':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft':
        return 'Borrador';
      case 'submitted':
        return 'Enviado';
      case 'accepted':
        return 'Aprobado';
      case 'denied':
        return 'Rechazado';
      case 'distributed':
        return 'Distribuido';
      default:
        return 'Desconocido';
    }
  };

  const getComparisonTypeLabel = (type?: string) => {
    switch (type) {
      case 'byWeeks':
        return 'Por Semanas';
      case 'byShips':
        return 'Por Naves';
      case 'custom':
        return 'Personalizado';
      case 'industry':
        return 'Industria';
      case 'sinister_market':
        return 'Siniestro · vs Mercado';
      case 'sinister_exporter':
        return 'Siniestro · vs Exportadora';
      case 'sinister_industry':
        return 'Siniestro · vs Industria';
      case 'manual_injection':
        return 'Inyección manual';
      default:
        return 'No especificado';
    }
  };

  const getSinisterFilterLabel = (filter?: string) => {
    switch (filter) {
      case 'all':
        return 'Todos';
      case 'sinister':
        return 'Solo Siniestros';
      case 'non_sinister':
        return 'Sin Siniestros';
      default:
        return 'No especificado';
    }
  };

  const getShipmentTypeLabel = (type?: string) => {
    switch (type) {
      case 'both':
        return 'Todos';
      case 'A':
        return 'Solo Aéreos';
      case 'M':
        return 'Solo Marítimos';
      case 'T':
        return 'Solo Terrestres';
      default:
        return 'No especificado';
    }
  };

  const fmt = (value: number) => {
    if (claim?.currency && claim.currency !== 'USD') {
      const formatted = value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `${formatted} ${claim.currency}`;
    }
    return formatMoneyUSD(value);
  };

  const handleBack = () => {
    setLocation('/settlements/claims');
  };

  const isUSD = !claim?.currency || claim?.currency === 'USD';

  const distributionPreview = useMemo(() => {
    if (!claim?.claims_details || !claim.total_accepted_usd) return null;

    const totalAccepted = claim.total_accepted_usd;

    const rows = claim.claims_details.map((detail) => {
      const ownPerKg =
        detail.own_value_per_kg ??
        (detail.settlements_details?.kilos &&
        detail.settlements_details.usd_total_fob
          ? detail.settlements_details.usd_total_fob /
            detail.settlements_details.kilos
          : null);
      const kilos = detail.settlements_details?.kilos || 0;
      const difference =
        ownPerKg != null
          ? (ownPerKg - detail.comparable_fob_per_kg) * kilos
          : null;
      return { detail, difference };
    });

    const negativeRows = rows.filter(
      (r) => r.difference != null && r.difference < 0
    );
    const totalNegative = negativeRows.reduce(
      (sum, r) => sum + Math.abs(r.difference!),
      0
    );

    if (totalNegative === 0) return null;

    const ratio = totalAccepted / totalNegative;

    const distributions = negativeRows.map(({ detail, difference }) => {
      const portion = Math.abs(difference!) * ratio;
      const rate =
        exchangeRateMode === 'fixed'
          ? parseFloat(fixedExchangeRate) || 0
          : detail.settlements_details?.settlements?.exchange_rate || 0;

      const amountUsd = isUSD ? portion : rate > 0 ? portion / rate : 0;
      const amountLocal = isUSD ? portion * rate : portion;

      return {
        detail,
        difference: difference!,
        portion,
        amountUsd,
        amountLocal,
        rate,
      };
    });

    return {
      totalAccepted,
      negativeCount: negativeRows.length,
      totalNegative,
      ratio,
      distributions,
    };
  }, [claim, exchangeRateMode, fixedExchangeRate, isUSD]);

  const handleDistribute = async () => {
    if (!claim || !distributionPreview) return;

    setIsDistributing(true);
    try {
      const groupId = crypto.randomUUID();

      const sdrRows = distributionPreview.distributions.map((d) => ({
        settlement_detail_id: d.detail.settlement_detail_id,
        revenue_type: 'claim_compensation',
        description: `Compensación #${claim.id}`,
        claim_id: claim.id,
        group_id: groupId,
        created_by: userProfile?.id ? Number(userProfile.id) : undefined,
        amount_usd: d.amountUsd,
        amount_local: d.amountLocal,
      }));

      const { error: insertError } = await supabase
        .from('settlement_detail_revenues')
        .insert(sdrRows);
      if (insertError) throw insertError;

      // Recalculate total_other_revenue_local for each affected settlement
      const uniqueSettlementIds = [
        ...new Set(
          distributionPreview.distributions
            .map((d) => d.detail.settlements_details?.settlements?.id)
            .filter((id): id is number => id != null)
        ),
      ];

      for (const settlementId of uniqueSettlementIds) {
        const { data: details } = await supabase
          .from('settlements_details')
          .select('id')
          .eq('settlement_id', settlementId);

        if (details && details.length > 0) {
          const detailIds = details.map((d) => d.id);
          const { data: revenues } = await supabase
            .from('settlement_detail_revenues')
            .select('amount_local')
            .in('settlement_detail_id', detailIds);

          const total = (revenues || []).reduce(
            (sum: number, r: { amount_local?: number | null }) =>
              sum + (r.amount_local || 0),
            0
          );
          await supabase
            .from('settlements')
            .update({ total_other_revenue_local: total })
            .eq('id', settlementId);
        }
      }

      // Update claim status to distributed
      await distributeClaimStatus(claim.id);

      // Refresh claim data
      const updatedClaim = await getClaimById(claim.id);
      setClaim(updatedClaim);

      onDistributeClose();
      toast.success('Distribución completada exitosamente');
    } catch (error) {
      console.error('Error distributing claim:', error);
      toast.error('Error al distribuir la compensación');
    } finally {
      setIsDistributing(false);
    }
  };

  const handleUndistribute = async () => {
    if (!claim) return;

    const confirmed = await confirm({
      title: '¿Borrar la distribución?',
      message:
        'Se eliminarán los ingresos distribuidos en las liquidaciones asociadas.',
      confirmLabel: 'Borrar',
      isDestructive: true,
    });
    if (!confirmed) return;

    setIsUndistributing(true);
    try {
      // 1. Find settlement_detail_revenues linked to this claim
      const { data: revenues, error: fetchError } = await supabase
        .from('settlement_detail_revenues')
        .select('id, settlement_detail_id')
        .eq('claim_id', claim.id);
      if (fetchError) throw fetchError;

      // 2. Get unique settlement IDs before deleting revenues
      const detailIds = [
        ...new Set((revenues || []).map((r) => r.settlement_detail_id)),
      ];
      let uniqueSettlementIds: number[] = [];

      if (detailIds.length > 0) {
        const { data: details } = await supabase
          .from('settlements_details')
          .select('settlement_id')
          .in('id', detailIds);

        uniqueSettlementIds = [
          ...new Set((details || []).map((d) => d.settlement_id)),
        ];
      }

      // 3. Delete the revenues
      const { error: deleteError } = await supabase
        .from('settlement_detail_revenues')
        .delete()
        .eq('claim_id', claim.id);
      if (deleteError) throw deleteError;

      // 4. Recalculate total_other_revenue_local for each affected settlement
      for (const settlementId of uniqueSettlementIds) {
        const { data: settlementDetails } = await supabase
          .from('settlements_details')
          .select('id')
          .eq('settlement_id', settlementId);

        if (settlementDetails && settlementDetails.length > 0) {
          const sdIds = settlementDetails.map((d) => d.id);
          const { data: remainingRevenues } = await supabase
            .from('settlement_detail_revenues')
            .select('amount_local')
            .in('settlement_detail_id', sdIds);

          const total = (remainingRevenues || []).reduce(
            (sum: number, r: { amount_local?: number | null }) =>
              sum + (r.amount_local || 0),
            0
          );
          await supabase
            .from('settlements')
            .update({ total_other_revenue_local: total })
            .eq('id', settlementId);
        }
      }

      // 5. Update claim status back to accepted
      await undistributeClaimStatus(claim.id);

      // 6. Refresh claim data
      const updatedClaim = await getClaimById(claim.id);
      setClaim(updatedClaim);
      if (claim.comparison_type === 'manual_injection') {
        setManualRevenues([]);
      }

      toast.success('Distribución eliminada exitosamente');
    } catch (error) {
      console.error('Error undistributing claim:', error);
      toast.error('Error al borrar la distribución');
    } finally {
      setIsUndistributing(false);
    }
  };

  const handleExportExcel = () => {
    if (!claim?.claims_details || claim.claims_details.length === 0) return;

    const valueLabel =
      claim.currency && claim.currency !== 'USD' ? 'Valor' : 'FOB';

    const rows = claim.claims_details.map((detail) => {
      const ownPerKg =
        detail.own_value_per_kg ??
        (detail.settlements_details?.kilos &&
        detail.settlements_details.usd_total_fob
          ? detail.settlements_details.usd_total_fob /
            detail.settlements_details.kilos
          : null);
      const kilos = detail.settlements_details?.kilos || 0;
      const diffPerKg =
        ownPerKg != null ? ownPerKg - detail.comparable_fob_per_kg : null;
      const diffTotal = diffPerKg != null && kilos ? diffPerKg * kilos : null;

      const row: Record<string, string | number> = {};

      if (claim.comparison_type === 'byWeeks') {
        const date =
          claim.date_grouping_type === 'date_sold'
            ? detail.settlements_details?.settlements?.date_sold
            : claim.date_grouping_type === 'ship_eta'
              ? detail.settlements_details?.settlements?.ship_trips?.eta
              : detail.settlements_details?.settlements?.ship_trips?.etd;
        row['Semana'] = getWeekKey(date) || '-';
      }
      if (
        claim.comparison_type === 'byShips' ||
        claim.comparison_type === 'industry'
      ) {
        const ship = detail.settlements_details?.settlements?.ship_trips?.ships;
        const shipType = ship?.type;
        const shipName = ship?.name || '-';
        const date =
          claim.date_grouping_type === 'date_sold'
            ? detail.settlements_details?.settlements?.date_sold
            : claim.date_grouping_type === 'ship_eta'
              ? detail.settlements_details?.settlements?.ship_trips?.eta
              : detail.settlements_details?.settlements?.ship_trips?.etd;
        const shipWeek = getWeekKey(date);
        row['Nave'] = ['A', 'T'].includes(shipType)
          ? `${shipName}-${shipWeek}`
          : shipName;
      }

      row['Referencia'] =
        detail.settlements_details?.settlements?.reference_number || '-';
      row['Variedad'] = detail.settlements_details?.variety || '-';
      row['Calibre'] = detail.settlements_details?.caliber || '-';

      if (claim.product_definition?.includes('FORMAT')) {
        row['Formato'] = detail.settlements_details?.format || '-';
      }
      if (claim.product_definition?.includes('COLOR')) {
        row['Color'] = detail.settlements_details?.color || '-';
      }
      if (claim.product_definition?.includes('LABEL')) {
        row['Etiqueta'] = detail.settlements_details?.label || '-';
      }

      row['Kilos Propios'] = kilos;
      if (claim.comparison_type !== 'industry') {
        row[`${valueLabel}/KG Propio`] =
          ownPerKg != null ? Math.round(ownPerKg * 10000) / 10000 : '-';
      }
      row['Kilos Comparables'] = detail.comparable_kilos;
      if (claim.comparison_type !== 'industry') {
        row[`${valueLabel}/KG Comparable`] =
          Math.round(detail.comparable_fob_per_kg * 10000) / 10000;
      }
      row['Diferencia por Kilo'] =
        diffPerKg != null ? Math.round(diffPerKg * 10000) / 10000 : '-';
      row['Diferencia Total'] =
        diffTotal != null ? Math.round(diffTotal * 100) / 100 : '-';

      return row;
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Detalles');
    XLSX.writeFile(wb, `Compensacion_${claim.id}_Detalles.xlsx`);
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (!claim) {
    return (
      <PageLayout>
        <EmptyState
          title='Compensación no encontrada'
          icon='tabler:file-x'
          action={
            <Button color='primary' variant='light' onClick={handleBack}>
              Volver a la lista
            </Button>
          }
        />
      </PageLayout>
    );
  }

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      <PageLayout>
        <BackButton href='/settlements/claims' label='Volver' />
        <Header
          title={`Compensación #${claim.id}`}
          titleAddon={
            <Chip color={getStatusColor(claim.status)} variant='flat'>
              {getStatusLabel(claim.status)}
            </Chip>
          }
        />

        <div className='grid grid-cols-1 gap-6'>
          {/* Claim Information Card */}
          <Card shadow='sm'>
            <CardHeader className='flex flex-col gap-2 pb-0'>
              <div className='flex justify-between items-center w-full'>
                <h2 className='text-xl font-bold'>
                  Información de la Compensación
                </h2>
                <div className='flex gap-2 items-center'>
                  {claim.status === 'distributed' && claim.distributed_at && (
                    <>
                      <span className='text-sm text-default-400'>
                        Distribuido el{' '}
                        {new Date(claim.distributed_at).toLocaleDateString()}
                      </span>
                      <Button
                        color='danger'
                        variant='flat'
                        onClick={handleUndistribute}
                        isLoading={isUndistributing}
                        startContent={
                          !isUndistributing ? (
                            <Icon icon='mdi:delete-outline' />
                          ) : undefined
                        }
                      >
                        Borrar Distribución
                      </Button>
                    </>
                  )}
                  {claim.comparison_type !== 'manual_injection' &&
                    claim.status === 'accepted' &&
                    claim.total_accepted_usd &&
                    claim.total_accepted_usd > 0 && (
                      <Button
                        color='primary'
                        onClick={onDistributeOpen}
                        startContent={<Icon icon='solar:chart-square-linear' />}
                      >
                        Distribuir
                      </Button>
                    )}
                </div>
              </div>
            </CardHeader>

            <CardBody>
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4'>
                <div>
                  <p className='text-sm text-default-400'>Cliente</p>
                  <p className='font-semibold'>{claim.receiver}</p>
                </div>
                <div>
                  <p className='text-sm text-default-400'>Descripción</p>
                  <p className='font-semibold'>{claim.description}</p>
                </div>
                <div>
                  <p className='text-sm text-default-400'>Temporada</p>
                  <p className='font-semibold'>{claim.season}</p>
                </div>
                <div>
                  <p className='text-sm text-default-400'>Fecha Creación</p>
                  <p className='font-semibold'>
                    {new Date(claim.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className='text-sm text-default-400'>Fecha Enviado</p>
                  <p className='font-semibold'>
                    {claim.submitted_at
                      ? new Date(claim.submitted_at).toLocaleDateString()
                      : '-'}
                  </p>
                </div>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4'>
                <div>
                  <p className='text-sm text-default-400'>
                    Tipo de Comparativo
                  </p>
                  <p className='font-semibold'>
                    {getComparisonTypeLabel(claim.comparison_type)}
                  </p>
                </div>
                <div>
                  <p className='text-sm text-default-400'>Incoterm</p>
                  <p className='font-semibold'>{claim.incoterm || '-'}</p>
                </div>
                <div>
                  <p className='text-sm text-default-400'>
                    Filtro de Siniestros
                  </p>
                  <p className='font-semibold'>
                    {getSinisterFilterLabel(claim.sinister_filter)}
                  </p>
                </div>
                <div>
                  <p className='text-sm text-default-400'>Tipo de Flete</p>
                  <p className='font-semibold'>
                    {getShipmentTypeLabel(claim.shipment_type)}
                  </p>
                </div>
                <div>
                  <p className='text-sm text-default-400'>
                    Definición de Producto
                  </p>
                  <p className='font-semibold'>
                    {claim.product_definition || '-'}
                  </p>
                </div>
                <div>
                  <p className='text-sm text-default-400'>Fecha Utilizada</p>
                  <p className='font-semibold'>
                    {claim.comparison_type === 'sinister_market' &&
                    claim.market_reference_date
                      ? new Date(
                          claim.market_reference_date
                        ).toLocaleDateString()
                      : claim.date_grouping_type || '-'}
                  </p>
                </div>
                {(claim.comparison_type === 'industry' ||
                  claim.comparison_type === 'sinister_industry') &&
                  claim.pool_methodology && (
                    <div>
                      <p className='text-sm text-default-400'>
                        Ponderación Industria
                      </p>
                      <p className='font-semibold'>
                        {claim.pool_methodology === 'by_volume'
                          ? 'Por Volumen'
                          : 'Por Exportadora'}
                      </p>
                    </div>
                  )}
                {claim.comparison_type === 'sinister_market' &&
                  claim.exchange_rate_used != null && (
                    <div>
                      <p className='text-sm text-default-400'>
                        Tipo de Cambio (RMB → USD)
                      </p>
                      <p className='font-semibold'>
                        {Number(claim.exchange_rate_used).toFixed(4)}
                      </p>
                    </div>
                  )}
                {claim.include_revenues_main && (
                  <div>
                    <p className='text-sm text-default-400'>
                      Ingresos Adicionales en Cliente Principal
                    </p>
                    <p className='font-semibold'>Incluidos</p>
                  </div>
                )}
                {claim.include_revenues_comparison && (
                  <div>
                    <p className='text-sm text-default-400'>
                      Ingresos Adicionales en Clientes Comparados
                    </p>
                    <p className='font-semibold'>Incluidos</p>
                  </div>
                )}
                {claim.include_market_in_air && (
                  <div>
                    <p className='text-sm text-default-400'>
                      Mercado en Agrupación Aéreos
                    </p>
                    <p className='font-semibold'>Sí</p>
                  </div>
                )}
              </div>

              {claim.market_filter && claim.market_filter.length > 0 && (
                <div className='mb-4'>
                  <p className='text-sm text-default-400'>Mercados Filtrados</p>
                  <div className='flex flex-wrap gap-2 mt-1'>
                    {claim.market_filter.map((market, index) => (
                      <Chip key={index} size='sm' variant='flat'>
                        {market}
                      </Chip>
                    ))}
                  </div>
                </div>
              )}

              {claim.comparison_type != 'custom' &&
                claim.comparison_type !== 'manual_injection' && (
                <div className='mb-4'>
                  <p className='text-sm text-default-400'>
                    {claim.comparison_type === 'industry'
                      ? 'Comparado Contra'
                      : 'Clientes Comparados'}
                  </p>
                  <div className='flex flex-wrap gap-2 mt-1'>
                    {claim.comparison_type === 'industry' ? (
                      <Chip size='sm' variant='flat'>
                        Industria (
                        {claim.comparison_target === 'q1'
                          ? 'Q1'
                          : claim.comparison_target === 'q3'
                            ? 'Q3'
                            : 'Mediana'}
                        )
                      </Chip>
                    ) : (
                      <>
                        {claim.comparison_clients &&
                          claim.comparison_clients.length > 0 &&
                          claim.comparison_clients.map((client, index) => (
                            <Chip key={index} size='sm' variant='flat'>
                              {client}
                            </Chip>
                          ))}
                        {claim.comparison_clients &&
                          claim.comparison_clients.length == 0 && (
                            <Chip size='sm' variant='flat'>
                              Todos
                            </Chip>
                          )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {claim.comparison_type === 'industry' &&
                claim.filtered_clients &&
                claim.filtered_clients.length > 0 && (
                  <div className='mb-4'>
                    <p className='text-sm text-default-400'>
                      Clientes Filtrados
                    </p>
                    <div className='flex flex-wrap gap-2 mt-1'>
                      {claim.filtered_clients.map((client) => (
                        <Chip
                          key={client}
                          size='sm'
                          variant='flat'
                          color='secondary'
                        >
                          {client}
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}

              {claim.comparison_type === 'industry' &&
                claim.filtered_destination_countries &&
                claim.filtered_destination_countries.length > 0 && (
                  <div className='mb-4'>
                    <p className='text-sm text-default-400'>
                      Países de Destino Filtrados
                    </p>
                    <div className='flex flex-wrap gap-2 mt-1'>
                      {claim.filtered_destination_countries.map((country) => (
                        <Chip
                          key={country}
                          size='sm'
                          variant='flat'
                          color='secondary'
                        >
                          {country}
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}

              {claim.comparison_type === 'industry' &&
                claim.filtered_varieties &&
                claim.filtered_varieties.length > 0 && (
                  <div className='mb-4'>
                    <p className='text-sm text-default-400'>
                      Variedades Filtradas
                    </p>
                    <div className='flex flex-wrap gap-2 mt-1'>
                      {claim.filtered_varieties.map((variety, index) => (
                        <Chip
                          key={index}
                          size='sm'
                          variant='flat'
                          color='secondary'
                        >
                          {variety}
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}

              {claim.comparison_type === 'industry' &&
                claim.filtered_calibers &&
                claim.filtered_calibers.length > 0 && (
                  <div className='mb-4'>
                    <p className='text-sm text-default-400'>
                      Calibres Filtrados
                    </p>
                    <div className='flex flex-wrap gap-2 mt-1'>
                      {claim.filtered_calibers.map((caliber) => (
                        <Chip
                          key={caliber}
                          size='sm'
                          variant='flat'
                          color='secondary'
                        >
                          {caliber}
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}

              {claim.comparison_type === 'industry' &&
                claim.filtered_ship_trips &&
                claim.filtered_ship_trips.length > 0 && (
                  <div className='mb-4'>
                    <p className='text-sm text-default-400'>
                      Viajes de Nave Filtrados
                    </p>
                    <div className='flex flex-wrap gap-2 mt-1'>
                      {claim.filtered_ship_trips.map((tripId, index) => (
                        <Chip
                          key={index}
                          size='sm'
                          variant='flat'
                          color='secondary'
                        >
                          {shipTripLabels[tripId] || `Trip #${tripId}`}
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}

              {claim.notes && (
                <div className='mb-4'>
                  <p className='text-sm text-default-400'>Notas</p>
                  <p className='mt-1'>{claim.notes}</p>
                </div>
              )}

              <div className='mb-4'>
                <p className='text-sm text-default-400'>
                  Monto Total Reclamado
                </p>
                <p
                  className={`text-xl font-bold ${claim.total_claim_usd && claim.total_claim_usd < 0 ? 'text-danger-500' : 'text-success-500'}`}
                >
                  {claim.total_claim_usd ? fmt(claim.total_claim_usd) : 'N/A'}
                </p>
              </div>
            </CardBody>
          </Card>

          {claim.comparison_type === 'manual_injection' && (
            <Card shadow='sm'>
              <CardHeader className='flex justify-between items-center'>
                <h2 className='text-xl font-bold'>Detalle de Inyección</h2>
                <Chip size='sm' variant='flat' color='secondary'>
                  {manualRevenues.length} líneas
                </Chip>
              </CardHeader>
              <CardBody>
                {manualRevenues.length === 0 ? (
                  <p className='text-default-400'>
                    No hay ingresos asociados a esta inyección.
                  </p>
                ) : (
                  <Table aria-label='Manual injection details table'>
                    <TableHeader>
                      <TableColumn>GRUPO</TableColumn>
                      <TableColumn>LIQUIDACIÓN</TableColumn>
                      <TableColumn>CONTENEDOR</TableColumn>
                      <TableColumn>VARIEDAD</TableColumn>
                      <TableColumn>CALIBRE</TableColumn>
                      <TableColumn>COLOR</TableColumn>
                      <TableColumn>FORMATO</TableColumn>
                      <TableColumn>ETIQUETA</TableColumn>
                      <TableColumn>KILOS</TableColumn>
                      <TableColumn>DISTRIBUCIÓN</TableColumn>
                      <TableColumn>MONTO USD</TableColumn>
                      <TableColumn>MONTO LOCAL</TableColumn>
                    </TableHeader>
                    <TableBody>
                      {manualRevenues.map((revenue) => (
                        <TableRow key={revenue.id}>
                          <TableCell>{revenue.description || '-'}</TableCell>
                          <TableCell>
                            {revenue.settlements_details?.settlements
                              ?.reference_number || '-'}
                          </TableCell>
                          <TableCell>
                            {revenue.settlements_details?.settlements
                              ?.container_number || '-'}
                          </TableCell>
                          <TableCell>
                            {revenue.settlements_details?.variety || '-'}
                          </TableCell>
                          <TableCell>
                            {revenue.settlements_details?.caliber || '-'}
                          </TableCell>
                          <TableCell>
                            {revenue.settlements_details?.color || '-'}
                          </TableCell>
                          <TableCell>
                            {revenue.settlements_details?.format || '-'}
                          </TableCell>
                          <TableCell>
                            {revenue.settlements_details?.label || '-'}
                          </TableCell>
                          <TableCell>
                            {revenue.settlements_details?.kilos?.toLocaleString() ||
                              '-'}
                          </TableCell>
                          <TableCell>
                            {revenue.distribution === 'bySales'
                              ? 'Por Venta'
                              : 'Por Kg'}
                          </TableCell>
                          <TableCell>
                            {formatMoneyUSD(revenue.amount_usd || 0)}
                          </TableCell>
                          <TableCell>
                            {(revenue.amount_local || 0).toLocaleString(
                              undefined,
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardBody>
            </Card>
          )}

          {/* Claim Details Table */}
          {claim.claims_details && claim.claims_details.length > 0 && (
            <Card shadow='sm'>
              <CardHeader className='flex justify-between items-center'>
                <h2 className='text-xl font-bold'>Detalles de Comparación</h2>
                <Button
                  variant='flat'
                  size='sm'
                  onClick={handleExportExcel}
                  startContent={<Icon icon='mdi:file-excel-outline' />}
                >
                  Exportar Excel
                </Button>
              </CardHeader>
              <CardBody>
                <Table aria-label='Claim comparison details table'>
                  <TableHeader>
                    {(() => {
                      const columns = [];
                      if (claim.comparison_type == 'byWeeks') {
                        columns.push(
                          <TableColumn key='week'>SEMANA</TableColumn>
                        );
                      }
                      if (
                        claim.comparison_type == 'byShips' ||
                        claim.comparison_type == 'industry'
                      ) {
                        columns.push(
                          <TableColumn key='ship'>NAVE</TableColumn>
                        );
                      }
                      columns.push(
                        <TableColumn key='ref'>REFERENCIA</TableColumn>
                      );
                      columns.push(
                        <TableColumn key='var'>VARIEDAD</TableColumn>
                      );
                      columns.push(
                        <TableColumn key='cal'>CALIBRE</TableColumn>
                      );
                      if (claim.product_definition?.includes('FORMAT')) {
                        columns.push(
                          <TableColumn key='format'>FORMATO</TableColumn>
                        );
                      }
                      if (claim.product_definition?.includes('COLOR')) {
                        columns.push(
                          <TableColumn key='color'>COLOR</TableColumn>
                        );
                      }
                      if (claim.product_definition?.includes('LABEL')) {
                        columns.push(
                          <TableColumn key='label'>ETIQUETA</TableColumn>
                        );
                      }
                      columns.push(
                        <TableColumn key='kilos'>KILOS PROPIOS</TableColumn>
                      );
                      if (claim.comparison_type !== 'industry') {
                        const valLabel =
                          claim.currency && claim.currency !== 'USD'
                            ? 'VALOR/KG PROPIO'
                            : 'FOB/KG PROPIO';
                        columns.push(
                          <TableColumn key='ownPerKg'>{valLabel}</TableColumn>
                        );
                      }
                      columns.push(
                        <TableColumn key='compKilos'>
                          KILOS COMPARABLES
                        </TableColumn>
                      );
                      if (claim.comparison_type !== 'industry') {
                        const valLabel =
                          claim.currency && claim.currency !== 'USD'
                            ? 'VALOR/KG COMPARABLE'
                            : 'FOB/KG COMPARABLE';
                        columns.push(
                          <TableColumn key='compPerKg'>{valLabel}</TableColumn>
                        );
                      }
                      columns.push(
                        <TableColumn key='diffKg'>
                          DIFERENCIA POR KILO
                        </TableColumn>
                      );
                      columns.push(
                        <TableColumn key='diffTotal'>
                          DIFERENCIA TOTAL
                        </TableColumn>
                      );
                      return columns;
                    })()}
                  </TableHeader>
                  <TableBody>
                    {claim.claims_details.map((detail) => (
                      <TableRow key={detail.id}>
                        {(() => {
                          const cells = [];
                          if (claim.comparison_type == 'byWeeks') {
                            const date =
                              claim.date_grouping_type == 'date_sold'
                                ? detail.settlements_details?.settlements
                                    ?.date_sold
                                : claim.date_grouping_type == 'ship_eta'
                                  ? detail.settlements_details?.settlements
                                      ?.ship_trips?.eta
                                  : detail.settlements_details?.settlements
                                      ?.ship_trips?.etd;
                            const week = getWeekKey(date);
                            cells.push(
                              <TableCell key='week'>{week || '-'}</TableCell>
                            );
                          }
                          if (
                            claim.comparison_type == 'byShips' ||
                            claim.comparison_type == 'industry'
                          ) {
                            const ship =
                              detail.settlements_details?.settlements
                                ?.ship_trips?.ships;
                            const shipType = ship?.type;
                            const shipName = ship?.name;
                            const date =
                              claim.date_grouping_type == 'date_sold'
                                ? detail.settlements_details?.settlements
                                    ?.date_sold
                                : claim.date_grouping_type == 'ship_eta'
                                  ? detail.settlements_details?.settlements
                                      ?.ship_trips?.eta
                                  : detail.settlements_details?.settlements
                                      ?.ship_trips?.etd;
                            const shipWeek = getWeekKey(date);
                            cells.push(
                              <TableCell key='ship'>
                                {['A', 'T'].includes(shipType)
                                  ? `${shipName}-${shipWeek}`
                                  : shipName}
                              </TableCell>
                            );
                          }
                          cells.push(
                            <TableCell key='ref'>
                              {detail.settlements_details?.settlements
                                ?.reference_number || '-'}
                            </TableCell>
                          );
                          cells.push(
                            <TableCell key='var'>
                              {detail.settlements_details?.variety || '-'}
                            </TableCell>
                          );
                          cells.push(
                            <TableCell key='cal'>
                              {detail.settlements_details?.caliber || '-'}
                            </TableCell>
                          );
                          if (claim.product_definition?.includes('FORMAT')) {
                            cells.push(
                              <TableCell key='format'>
                                {detail.settlements_details?.format || '-'}
                              </TableCell>
                            );
                          }
                          if (claim.product_definition?.includes('COLOR')) {
                            cells.push(
                              <TableCell key='color'>
                                {detail.settlements_details?.color || '-'}
                              </TableCell>
                            );
                          }
                          if (claim.product_definition?.includes('LABEL')) {
                            cells.push(
                              <TableCell key='label'>
                                {detail.settlements_details?.label || '-'}
                              </TableCell>
                            );
                          }
                          cells.push(
                            <TableCell key='kilos'>
                              {detail.settlements_details?.kilos?.toLocaleString() ||
                                '-'}
                            </TableCell>
                          );

                          const ownPerKg =
                            detail.own_value_per_kg ??
                            (detail.settlements_details?.kilos &&
                            detail.settlements_details.usd_total_fob
                              ? detail.settlements_details.usd_total_fob /
                                detail.settlements_details.kilos
                              : null);

                          if (claim.comparison_type !== 'industry') {
                            cells.push(
                              <TableCell key='ownPerKg'>
                                {ownPerKg != null ? fmt(ownPerKg) : '-'}
                              </TableCell>
                            );
                          }
                          cells.push(
                            <TableCell key='compKilos'>
                              {detail.comparable_kilos.toLocaleString()} kg
                            </TableCell>
                          );
                          if (claim.comparison_type !== 'industry') {
                            cells.push(
                              <TableCell key='compPerKg'>
                                {fmt(detail.comparable_fob_per_kg)}
                              </TableCell>
                            );
                          }
                          cells.push(
                            <TableCell key='diffKg'>
                              {ownPerKg != null
                                ? fmt(ownPerKg - detail.comparable_fob_per_kg)
                                : '-'}
                            </TableCell>
                          );
                          const kilos = detail.settlements_details?.kilos;
                          cells.push(
                            <TableCell key='diffTotal'>
                              {ownPerKg != null && kilos
                                ? fmt(
                                    (ownPerKg - detail.comparable_fob_per_kg) *
                                      kilos
                                  )
                                : '-'}
                            </TableCell>
                          );
                          return cells;
                        })()}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardBody>
            </Card>
          )}
        </div>
      </PageLayout>

      {/* Distribute Modal */}
      <Modal
        isOpen={isDistributeOpen}
        onClose={onDistributeClose}
        size='4xl'
        scrollBehavior='inside'
      >
        <ModalContent>
          <ModalHeader>Distribuir Compensación #{claim.id}</ModalHeader>
          <ModalBody>
            {distributionPreview ? (
              <div className='space-y-4'>
                {/* Summary */}
                <div className='bg-default-100 rounded-lg p-4'>
                  <p className='text-sm text-default-400'>
                    Monto total aceptado
                  </p>
                  <p className='text-xl font-bold'>
                    {fmt(distributionPreview.totalAccepted)}
                  </p>
                </div>

                {/* Exchange rate selection */}
                <div>
                  <p className='font-semibold mb-2'>
                    {isUSD ? 'Conversión a moneda local' : 'Conversión a USD'}
                  </p>
                  <RadioGroup
                    value={exchangeRateMode}
                    onValueChange={(val) =>
                      setExchangeRateMode(val as 'fixed' | 'per_settlement')
                    }
                  >
                    <Radio value='per_settlement'>
                      Usar tipo de cambio de cada liquidación
                    </Radio>
                    <Radio value='fixed'>Definir tipo de cambio fijo</Radio>
                  </RadioGroup>
                  {exchangeRateMode === 'fixed' && (
                    <Input
                      type='number'
                      label='Tipo de cambio'
                      placeholder='Ej: 950'
                      value={fixedExchangeRate}
                      onChange={(e) => setFixedExchangeRate(e.target.value)}
                      className='mt-2 max-w-xs'
                    />
                  )}
                </div>

                {/* Distribution info */}
                <div className='bg-default-50 border border-default-200 rounded-lg p-3'>
                  <p className='text-sm'>
                    <strong>{distributionPreview.negativeCount}</strong> filas
                    negativas con un total de{' '}
                    <strong>{fmt(distributionPreview.totalNegative)}</strong>.{' '}
                    Ratio de distribución:{' '}
                    <strong>
                      {(distributionPreview.ratio * 100).toFixed(2)}%
                    </strong>
                  </p>
                </div>

                {/* Preview table */}
                <Table aria-label='Distribution preview' isCompact>
                  <TableHeader>
                    <TableColumn>VARIEDAD</TableColumn>
                    <TableColumn>CALIBRE</TableColumn>
                    <TableColumn>KILOS</TableColumn>
                    <TableColumn>
                      DIFERENCIA ({claim.currency || 'USD'})
                    </TableColumn>
                    <TableColumn>
                      MONTO ASIGNADO ({claim.currency || 'USD'})
                    </TableColumn>
                    <TableColumn>
                      {isUSD ? 'MONTO LOCAL' : 'MONTO USD'}
                    </TableColumn>
                    <TableColumn>T/C</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {[
                      ...distributionPreview.distributions.map((d, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            {d.detail.settlements_details?.variety || '-'}
                          </TableCell>
                          <TableCell>
                            {d.detail.settlements_details?.caliber || '-'}
                          </TableCell>
                          <TableCell>
                            {d.detail.settlements_details?.kilos?.toLocaleString() ||
                              '-'}
                          </TableCell>
                          <TableCell className='text-danger-500'>
                            {fmt(d.difference)}
                          </TableCell>
                          <TableCell className='text-success-500'>
                            {fmt(d.portion)}
                          </TableCell>
                          <TableCell>
                            {isUSD
                              ? d.amountLocal.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })
                              : formatMoneyUSD(d.amountUsd)}
                          </TableCell>
                          <TableCell>
                            {d.rate
                              ? d.rate.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })
                              : '-'}
                          </TableCell>
                        </TableRow>
                      )),
                      <TableRow
                        key='totals'
                        className='font-bold bg-default-100'
                      >
                        <TableCell>TOTAL</TableCell>
                        <TableCell>{''}</TableCell>
                        <TableCell>
                          {distributionPreview.distributions
                            .reduce(
                              (s, d) =>
                                s + (d.detail.settlements_details?.kilos || 0),
                              0
                            )
                            .toLocaleString()}
                        </TableCell>
                        <TableCell className='text-danger-500'>
                          {fmt(
                            distributionPreview.distributions.reduce(
                              (s, d) => s + d.difference,
                              0
                            )
                          )}
                        </TableCell>
                        <TableCell className='text-success-500'>
                          {fmt(
                            distributionPreview.distributions.reduce(
                              (s, d) => s + d.portion,
                              0
                            )
                          )}
                        </TableCell>
                        <TableCell>
                          {isUSD
                            ? distributionPreview.distributions
                                .reduce((s, d) => s + d.amountLocal, 0)
                                .toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })
                            : formatMoneyUSD(
                                distributionPreview.distributions.reduce(
                                  (s, d) => s + d.amountUsd,
                                  0
                                )
                              )}
                        </TableCell>
                        <TableCell>{''}</TableCell>
                      </TableRow>,
                    ]}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className='text-default-400'>
                No hay filas negativas para distribuir.
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onClick={onDistributeClose}>
              Cancelar
            </Button>
            <Button
              color='primary'
              onClick={handleDistribute}
              isLoading={isDistributing}
              isDisabled={
                !distributionPreview ||
                (exchangeRateMode === 'fixed' &&
                  (!fixedExchangeRate || parseFloat(fixedExchangeRate) <= 0))
              }
            >
              Confirmar Distribución
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default ClaimDetailPage;
